/**
 * Indicator Chart Component
 *
 * Single-big-number layout for `chart_type='indicator'`. Visually distinct
 * from `KPIIndicator` — wraps the value in a `Card` with a circular SVG
 * progress ring when a target is configured.
 */

import { useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { useIndicatorData } from './useIndicatorData';

interface IndicatorChartComponentProps {
  indicatorId?: number;
}

const RADIUS = 52;
const STROKE = 8;
const SIZE = (RADIUS + STROKE) * 2;

function CircularProgress({ ratio }: { ratio: number }) {
  const clamped = Math.max(0, Math.min(1, ratio));
  const circumference = 2 * Math.PI * RADIUS;
  const offset = circumference * (1 - clamped);

  return (
    <svg width={SIZE} height={SIZE} role="img" aria-label="Indicator progress">
      <circle
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        stroke="currentColor"
        className="text-muted/30"
        strokeWidth={STROKE}
        fill="none"
      />
      <circle
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        stroke="currentColor"
        className="text-primary transition-all"
        strokeWidth={STROKE}
        strokeLinecap="round"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
      />
    </svg>
  );
}

export function IndicatorChartComponent({ indicatorId }: IndicatorChartComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);

  const kpi = live?.kpi;
  const numericValue = useMemo(() => {
    if (!kpi) return null;
    const n = typeof kpi.value === 'number' ? kpi.value : Number(kpi.value);
    return Number.isFinite(n) ? n : null;
  }, [kpi]);
  const target = typeof kpi?.target === 'number' ? kpi.target : null;
  const ratio = target !== null && target > 0 && numericValue !== null
    ? numericValue / target
    : null;

  if (loading && !kpi) {
    return (
      <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  if (!kpi) {
    return (
      <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
        No indicator data.
      </div>
    );
  }

  const display = numericValue !== null
    ? numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : String(kpi.value ?? '—');

  return (
    <Card className="border-0 shadow-none bg-transparent">
      <CardContent className="flex flex-col items-center justify-center p-4">
        {ratio !== null ? (
          <div className="relative" style={{ width: SIZE, height: SIZE }}>
            <CircularProgress ratio={ratio} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl font-bold leading-tight">{display}</div>
              {kpi.unit && <div className="text-xs text-muted-foreground">{kpi.unit}</div>}
            </div>
          </div>
        ) : (
          <div className="text-5xl font-bold tracking-tight">
            {display}
            {kpi.unit && <span className="text-2xl text-muted-foreground ml-1">{kpi.unit}</span>}
          </div>
        )}
        {kpi.label && (
          <div className="text-sm text-muted-foreground mt-3">{kpi.label}</div>
        )}
        {target !== null && (
          <div className="text-xs text-muted-foreground mt-1">
            Target: {target}{kpi.unit || ''}
          </div>
        )}
        {typeof kpi.change === 'number' && (
          <div className={`text-xs mt-1 ${kpi.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {kpi.change >= 0 ? '+' : ''}{kpi.change.toFixed(1)}% vs target
          </div>
        )}
      </CardContent>
    </Card>
  );
}
